import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { useAuth } from "../../context/AuthContext";

/**
 * Single super-admin login form. No campus selector — campus is chosen later in the app
 * shell; the admin session is global. Default credentials are documented in README + .env.example.
 */
export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login({ username, password });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Sign in failed. Please check your credentials.";
      setError(Array.isArray(msg) ? msg.join(", ") : String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        bgcolor: "background.default",
        p: 2,
        backgroundImage:
          "radial-gradient(circle at 20% 0%, rgba(37,99,235,0.08), transparent 40%), radial-gradient(circle at 100% 100%, rgba(15,23,42,0.06), transparent 50%)",
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 420, p: 1 }}>
        <CardContent>
          <Stack alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "12px",
                bgcolor: "#0f172a",
                color: "#fff",
                display: "grid",
                placeItems: "center",
              }}
            >
              <AdminPanelSettingsOutlinedIcon />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Unified Doc Admin
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              Sign in with the super-admin credentials to access notes,
              consultations, and evaluations across all campuses.
            </Typography>
          </Stack>

          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoFocus
                fullWidth
                autoComplete="username"
              />
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                autoComplete="current-password"
              />
              {error && <Alert severity="error">{error}</Alert>}
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                size="large"
                sx={{ mt: 0.5 }}
              >
                {submitting ? "Signing in..." : "Sign in"}
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
